import { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useUserPreferencesQuery, useUpdateUserPreferences } from '@/hooks/useUserPreferences';
import { USER_PREF_KEYS } from '@/services/userPreferencesService';
import { setActiveLocale, setActiveLocaleTag, languageToLocale } from '@/lib/locale';
import { loadAbpLocalization } from '@/services/abpLocalizationService';
import i18n from '@/i18n';

export type ThemeMode = 'light' | 'dark' | 'system';
export type AccentColor = 'navy' | 'blue' | 'teal' | 'purple' | 'orange' | 'red' | 'green';

export interface UserPreferencesContextType {
  isLoading: boolean;
  theme: ThemeMode;
  language: string;
  /** User's raw format-locale preference ('' = follow the display language). */
  formatLocalePref: string;
  /** Resolved BCP-47 format locale actually used for date/time/number formatting. */
  formatLocale: string;
  accentColor: AccentColor;
  landingPage: string;
  emailNotifications: boolean;
  updatePreference: (key: string, value: string) => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useUserPreferencesQuery();
  const mutation = useUpdateUserPreferences();

  const updatePreference = useCallback(
    (key: string, value: string) => {
      mutation.mutate({ [key]: value });
    },
    [mutation],
  );

  const language = data?.[USER_PREF_KEYS.language] || localStorage.getItem('nexo-language') || 'en';
  // Format-locale preference ('' = follow the display language). Persisted in localStorage as a
  // load-time seed so the very first render formats correctly before the DB fetch returns.
  const formatLocalePref =
    data?.[USER_PREF_KEYS.formatLocale] ?? localStorage.getItem('nexo-format-locale') ?? '';
  const formatLocale = formatLocalePref || languageToLocale(language);

  // Sync language locale (Accept-Language), i18next language, and ABP localization whenever the
  // display language changes. setActiveLocale must run before setActiveLocaleTag so the format
  // locale's "follow language" fallback sees the fresh language locale.
  useEffect(() => {
    setActiveLocale(language);
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    // Fetch ABP localization texts (DisplayName:*, Menu:*, Permission:*, etc.)
    // and merge into i18next so t() works for both bundled and server-provided keys
    loadAbpLocalization(language);
  }, [language]);

  // Sync the format locale (date/time/number formatting) whenever either the explicit
  // preference or the display language (its fallback) changes.
  useEffect(() => {
    setActiveLocaleTag(formatLocalePref);
  }, [formatLocalePref, language]);

  const value: UserPreferencesContextType = {
    isLoading,
    theme: (data?.[USER_PREF_KEYS.theme] as ThemeMode) || 'system',
    language,
    formatLocalePref,
    formatLocale,
    accentColor: (data?.[USER_PREF_KEYS.accentColor] as AccentColor) || 'navy',
    landingPage: data?.[USER_PREF_KEYS.landingPage] || '/',
    emailNotifications: data?.[USER_PREF_KEYS.emailNotifications] !== 'false',
    updatePreference,
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
